import { useState } from 'react';
import { useAtomValue } from 'jotai';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Copy, Key, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { EmptyState } from '@/components/glitch-ui/empty-state';
import { PrimaryButton } from '@/components/glitch-ui/primary-button';
import { ToggleSwitch } from '@/components/ui/toggle-switch';
import { useUserProfile } from '@/contexts/user.context';
import { useToast } from '@/hooks/useToast';
import { createApiKey, revokeApiKey, rotateApiKey } from '@/lib/apis';
import { apiKeysQueryOptions, useOrganizationMutationOptions } from '@/lib/query-options';
import { ApiKeyData, ApiKeyWithSecret, MembershipRole, OrganizationData } from '@/lib/types';
import { organizationDataAtom } from '@/store';
import { API_KEY_SCOPES, SCOPES_BY_ROLE } from './constants';

export const ApiKeysSettings = () => {
  const selectedOrganization = useAtomValue(organizationDataAtom);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [createdKey, setCreatedKey] = useState<ApiKeyWithSecret | null>(null);
  const [rotateTarget, setRotateTarget] = useState<ApiKeyData | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyData | null>(null);

  const orgId = selectedOrganization?.id;
  const role = selectedOrganization?.membership?.role;
  const allowedScopes = role ? SCOPES_BY_ROLE[role] : [];
  // The owner or an admin administers keys and the approval setting (#6828, owner-specific permissions are
  // billing only); anyone rotates or revokes their own key.
  const canAdminister = role === MembershipRole.OWNER || role === MembershipRole.ADMIN;

  const { data: keysData, isLoading } = useQuery(apiKeysQueryOptions(orgId));

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['api-keys', orgId] });

  const createMutation = useMutation({
    mutationFn: () => createApiKey(orgId!, newKeyName.trim(), selectedScopes),
    onSuccess: (data) => {
      setShowCreateDialog(false);
      setNewKeyName('');
      setSelectedScopes([]);
      setCreatedKey(data);
      invalidate();
    },
    onError: (error: Error) =>
      toast({ title: 'Key creation failed', description: error.message, variant: 'destructive' }),
  });

  const rotateMutation = useMutation({
    mutationFn: (keyId: string) => rotateApiKey(orgId!, keyId),
    onSuccess: (data) => {
      setRotateTarget(null);
      setCreatedKey(data);
      invalidate();
    },
    onError: (error: Error) => toast({ title: 'Rotation failed', description: error.message, variant: 'destructive' }),
  });

  const revokeMutation = useMutation({
    mutationFn: (keyId: string) => revokeApiKey(orgId!, keyId),
    onSuccess: () => {
      setRevokeTarget(null);
      invalidate();
    },
    onError: (error: Error) =>
      toast({ title: 'Revocation failed', description: error.message, variant: 'destructive' }),
  });

  const copySecret = async (secret: string) => {
    await navigator.clipboard.writeText(secret);
    toast({ title: 'Copied', description: 'The key is in your clipboard. It will not be shown again.' });
  };

  const toggleScope = (scope: string) =>
    setSelectedScopes((current) =>
      current.includes(scope) ? current.filter((s) => s !== scope) : [...current, scope],
    );

  const availableScopes = API_KEY_SCOPES.filter((scope) => allowedScopes.includes(scope.value));
  const allSelected =
    availableScopes.length > 0 && availableScopes.every((scope) => selectedScopes.includes(scope.value));
  const toggleAllScopes = () => setSelectedScopes(allSelected ? [] : availableScopes.map((scope) => scope.value));

  const keys = keysData?.keys ?? [];

  const approvalRequired = selectedOrganization?.approval_required ?? false;
  const organizationMutation = useOrganizationMutationOptions();
  const approvalMutation = useMutation({
    mutationFn: (required: boolean) =>
      organizationMutation.mutateAsync({ id: (selectedOrganization as OrganizationData).id, approval_required: required }),
    onError: (error: Error) =>
      toast({ title: 'Could not change the setting', description: error.message, variant: 'destructive' }),
  });

  // A key minted while impersonating would belong to the customer, reach every organization they are in and
  // outlive the session, so the server refuses it (#6625). Say so here rather than let the click fail.
  const { user } = useUserProfile();
  const isImpersonating = !!user?.impersonating_email;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">API keys</h2>
          <p className="text-sm text-gray-500">
            Keys authenticate the GlitchAds API. A key never exceeds the permissions of your role.
          </p>
        </div>
        <PrimaryButton
          label="Create key"
          onClick={() => setShowCreateDialog(true)}
          disabled={!orgId || isImpersonating}
        />
      </div>

      {/* The reason is rendered, not a tooltip: a disabled button takes no pointer events, so its title never fires. */}
      {isImpersonating && (
        <div
          className="mb-4 flex items-start gap-2 rounded-md border-l-4 p-3 text-sm"
          style={{ color: '#A16207', backgroundColor: '#FEF3C7', borderColor: '#FDE68A' }}
        >
          <AlertCircle className="h-4 w-4 shrink-0" />
          This session is impersonating a customer. A key created or rotated here would belong to them, reach every
          organization they are in and outlive the session, so only they can create one.
        </div>
      )}

      {/* The policy that governs what a key or a connected app may do unsupervised, so it lives with them
          rather than on a page about organizations (#6613). The owner's or an admin's (#6828). */}
      {canAdminister && (
        <div className="mb-6 rounded-lg border bg-white p-6 shadow-sm">
          <div className="flex items-start justify-between gap-6">
            <div>
              <h3 className="font-medium text-gray-900">Approve API and MCP writes</h3>
              <p className="mt-1 text-sm text-gray-500">
                Every change requested through a key or a connected app waits in Optimizations until somebody
                approves it. Reads and previews are never held.
              </p>
            </div>
            <ToggleSwitch
              mode="2-state"
              leftLabel="Off"
              rightLabel="On"
              value={approvalRequired ? 'right' : 'left'}
              onChange={(value) => approvalMutation.mutate(value === 'right')}
            />
          </div>
        </div>
      )}

      {isLoading && <p className="text-sm text-gray-500">Loading keys…</p>}
      {!isLoading && keys.length === 0 && (
        <EmptyState
          icon={<Key className="h-8 w-8 text-gray-400" />}
          title="No API keys yet"
          description="Keys created here are shown exactly once."
          action={
            <Button variant="outline" onClick={() => setShowCreateDialog(true)} disabled={!orgId || isImpersonating}>
              <Plus className="h-4 w-4 mr-2" />
              Create your first key
            </Button>
          }
        />
      )}

      {keys.length > 0 && (
        <div className="divide-y rounded-lg border bg-white shadow-sm">
          {keys.map((key) => (
            <div key={key.id} className="flex items-center justify-between gap-4 p-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">{key.name}</span>
                </div>
                <div className="font-mono text-sm text-gray-500">
                  {key.prefix}…{key.last_four}
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {key.scopes.map((scope) => (
                    <span key={scope} className="rounded-md bg-blue-100 px-2 py-1 text-xs text-blue-800">
                      {scope}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className="text-xs text-gray-400">
                  Expires {new Date(key.expires_at).toLocaleDateString()}
                </span>
                {!key.is_active && (
                  <span className="inline-flex items-center rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-700">
                    Revoked
                  </span>
                )}
                {key.is_active && (canAdminister || key.is_own) && (
                  <Button variant="outline" size="sm" disabled={isImpersonating} onClick={() => setRotateTarget(key)}>
                    <RefreshCw className="mr-1 h-4 w-4" />
                    Rotate
                  </Button>
                )}
                {key.is_active && (canAdminister || key.is_own) && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-700 hover:bg-red-50 hover:text-red-800"
                    onClick={() => setRevokeTarget(key)}
                  >
                    <Trash2 className="mr-1 h-4 w-4" />
                    Revoke
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create dialog: name + role-capped scope picker */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Create API key</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="api-key-name">Name</Label>
              <Input
                id="api-key-name"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="e.g. Reporting script"
                maxLength={255}
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Scopes</Label>
                <button
                  type="button"
                  className="text-xs font-medium text-[#290BD9] hover:text-[#2309C5]"
                  onClick={toggleAllScopes}
                >
                  {allSelected ? 'Deselect all' : 'Select all'}
                </button>
              </div>
              <p className="mb-2 text-xs text-gray-500">Your role allows the scopes listed below.</p>
              <div className="max-h-72 overflow-y-auto rounded-lg border bg-white">
                {availableScopes.map((scope) => {
                  const isChecked = selectedScopes.includes(scope.value);
                  return (
                    <div
                      key={scope.value}
                      className={`flex cursor-pointer items-start gap-3 border-b px-4 py-3 last:border-b-0 hover:bg-gray-50 ${
                        isChecked ? 'bg-[#290BD9]/10' : ''
                      }`}
                      onClick={() => toggleScope(scope.value)}
                    >
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={() => toggleScope(scope.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-0.5 data-[state=checked]:border-[#290BD9] data-[state=checked]:bg-[#290BD9] data-[state=checked]:text-white"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{scope.label}</div>
                        <div className="text-sm text-gray-500">{scope.description}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <PrimaryButton
              label={createMutation.isPending ? 'Creating…' : 'Create key'}
              onClick={() => createMutation.mutate()}
              disabled={!newKeyName.trim() || selectedScopes.length === 0 || createMutation.isPending}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Secret dialog: the one and only reveal */}
      <Dialog open={createdKey !== null} onOpenChange={(open) => !open && setCreatedKey(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Copy your key now</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500">
            This is the only time the key is shown. Store it in a secret manager.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all rounded-lg border bg-gray-50 px-3 py-2.5 font-mono text-sm text-gray-900">
              {createdKey?.secret}
            </code>
            <Button variant="outline" size="icon" onClick={() => createdKey && copySecret(createdKey.secret)}>
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <div className="space-y-1 text-xs text-gray-500">
            <p>• Your key is stored as a hash and can never be shown again</p>
            <p>• You can rotate or revoke it at any time from this settings page</p>
            <p>• The key never exceeds the permissions you granted it</p>
          </div>
          <DialogFooter>
            <PrimaryButton label="Done" onClick={() => setCreatedKey(null)} />
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rotate confirmation: cautionary, not destructive */}
      <ConformationModal
        isOpen={rotateTarget !== null}
        onClose={() => setRotateTarget(null)}
        onConfirm={() => rotateTarget && rotateMutation.mutate(rotateTarget.id)}
        title="Rotate this key?"
        description="A replacement key is issued immediately; the old key keeps working for 48 hours, then stops."
        confirmText="Rotate key"
        variant="warning"
      />

      {/* Revoke confirmation: destructive */}
      <ConformationModal
        isOpen={revokeTarget !== null}
        onClose={() => setRevokeTarget(null)}
        onConfirm={() => revokeTarget && revokeMutation.mutate(revokeTarget.id)}
        title="Revoke this key?"
        description="The key stops working immediately. Anything using it will start failing."
        confirmText="Revoke key"
      />
    </div>
  );
};
